import { useMemo, useEffect } from 'react';
import { ToastProvider, GlobalErrorBoundary, ToastContainer, LoginForm, AuthGuard, ThemeProvider } from '@textile-erp/ui';
import { BrowserRouter as Router, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuthContext } from './providers/AuthProvider';
import { ServiceProvider } from './providers/ServiceProvider';
import { NotificationProvider } from './providers/NotificationProvider';
import { MenuAccessProvider } from './providers/MenuAccessProvider';
import { AppLayout } from './app/layout/AppLayout';
import { ModuleRouter } from './app/routing/ModuleRouter';

import '@textile-erp/ui/dist/styles/theme.css';

// Paths that should NOT be stored as returnUrl
const SKIP_RETURN_PATHS = ['/', '/login', '/dashboard'];

// App Routes Component
function AppRoutes() {
  const { user, login, isLoading, error } = useAuthContext();
  const location = useLocation();
  const navigate = useNavigate();

  // When not authenticated: redirect to /login and carry the intended URL in location.state
  useEffect(() => {
    if (!isLoading && !user) {
      const currentPath = location.pathname + location.search;
      if (currentPath !== '/login') {
        const returnUrl = !SKIP_RETURN_PATHS.includes(location.pathname)
          ? currentPath
          : undefined;
        navigate('/login', { replace: true, state: { returnUrl } });
      }
    }
  }, [user, isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogin = async (credentials: { email: string; password: string }) => {
    await login(credentials);
    // Read returnUrl from location.state BEFORE React re-renders.
    // Calling navigate() here updates the history synchronously, so when
    // ModuleRouter renders it sees the returnUrl — not /login — which prevents
    // the <Navigate to="/dashboard"> from firing at all.
    const returnUrl = (location.state as { returnUrl?: string } | null)?.returnUrl;
    if (returnUrl) {
      navigate(returnUrl, { replace: true });
    }
  };

  const developmentDefaults = useMemo(() => ({
    email: "superadmin@hyatt.com",
    password: "admin123"
  }), []);

  return (
    <AuthGuard
      isAuthenticated={!!user}
      isLoading={isLoading}
      fallback={(() => {
        return (
          <div className="min-h-screen bg-theme-secondary flex items-center justify-center">
            <LoginForm
              onLogin={handleLogin}
              loading={isLoading}
              error={error || undefined}
              title="Sign in to Hyatt"
              description="Enter your credentials to access the system"
              developmentDefaults={developmentDefaults}
            />
          </div>
        );
      })()}
    >
      <MenuAccessProvider>
        <AppLayout>
          <ModuleRouter />
        </AppLayout>
      </MenuAccessProvider>
    </AuthGuard>
  );
}

function App() {

  return (
    <ThemeProvider>
      <GlobalErrorBoundary
        enableErrorTracking={true}
        enablePerformanceMonitoring={true}
        enableAutoReporting={true}
        maxRetries={3}
        retryDelay={2000}
        onError={(error, errorInfo) => {
          console.log('App-level error handler triggered:', error, errorInfo);
        }}
      >
        <ToastProvider>
          <Router>
            <AuthProvider>
              <ServiceProvider>
                <NotificationProvider>
                  <AppRoutes />
                  <ToastContainer />
                </NotificationProvider>
              </ServiceProvider>
            </AuthProvider>
          </Router>
        </ToastProvider>
      </GlobalErrorBoundary>
    </ThemeProvider>
  );
}

export default App;
